import React, { useState, useEffect, useCallback } from 'react'
import { UserPlus, Trash2, Users } from 'lucide-react'
import mdApi from '../api/mdApi'
import './CaregiversSection.css'

/**
 * Manage a patient's caregiver access grants.
 * Visible to clinicians (and patients managing their own record).
 */
function CaregiversSection({ patientId }) {
  const [grants, setGrants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [method, setMethod] = useState('email')
  const [contact, setContact] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [relationship, setRelationship] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await mdApi.getCaregivers(patientId)
      setGrants(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [patientId])

  useEffect(() => {
    load()
  }, [load])

  async function handleAdd(e) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await mdApi.addCaregiver(patientId, {
        contact: contact.trim(),
        method,
        display_name: displayName.trim() || undefined,
        relationship: relationship.trim() || undefined,
      })
      setContact('')
      setDisplayName('')
      setRelationship('')
      setShowForm(false)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRevoke(grantId) {
    if (!window.confirm('Revoke this caregiver\'s access?')) return
    setError(null)
    try {
      await mdApi.revokeCaregiver(patientId, grantId)
      await load()
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="detail-card caregivers-section">
      <div className="caregivers-header">
        <h2><Users size={18} /> Caregivers</h2>
        <button className="btn-add-caregiver" onClick={() => setShowForm((v) => !v)}>
          <UserPlus size={16} /> {showForm ? 'Cancel' : 'Add Caregiver'}
        </button>
      </div>

      {error && <div className="caregivers-error" role="alert">{error}</div>}

      {showForm && (
        <form className="caregiver-form" onSubmit={handleAdd}>
          <div className="caregiver-method-toggle">
            <button type="button" className={method === 'email' ? 'active' : ''} onClick={() => setMethod('email')}>Email</button>
            <button type="button" className={method === 'sms' ? 'active' : ''} onClick={() => setMethod('sms')}>Phone</button>
          </div>
          <input
            type={method === 'email' ? 'email' : 'tel'}
            placeholder={method === 'email' ? 'caregiver@example.com' : '+1 555 123 4567'}
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            required
          />
          <input type="text" placeholder="Display name (optional)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <input type="text" placeholder="Relationship (optional)" value={relationship} onChange={(e) => setRelationship(e.target.value)} />
          <button type="submit" className="btn-submit-caregiver" disabled={submitting}>
            {submitting ? 'Adding…' : 'Grant Access'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="caregivers-empty">Loading…</p>
      ) : grants.length === 0 ? (
        <p className="caregivers-empty">No caregivers have been granted access.</p>
      ) : (
        <ul className="caregivers-list">
          {grants.map((g) => (
            <li key={g.grant_id} className="caregiver-item">
              <div className="caregiver-info">
                <span className="caregiver-name">{g.display_name || g.caregiver_contact}</span>
                <span className="caregiver-meta">
                  {g.caregiver_contact}
                  {g.relationship ? ` · ${g.relationship}` : ''}
                  {` · ${g.status}`}
                </span>
              </div>
              <button className="btn-revoke-caregiver" onClick={() => handleRevoke(g.grant_id)} aria-label="Revoke caregiver access">
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default CaregiversSection
